import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Match } from '../../_shared/models/match.model';
import { Infringement } from '../../_shared/models/infringements.model';
import { InfringementType } from '../../_shared/models/enums/infringement-type';
import { ActionSheetController, IonRouterOutlet, ModalController, Platform } from '@ionic/angular';
import { BoardHelpService } from '../../match-tracker/tracker-board/board-help.service';
import { InterruptionReason } from '../../_shared/models/enums/interruption-reason';
import { MatchService } from '../../_core/services/match.service';
import { takeUntil } from 'rxjs/operators';
import { TimeItem } from '../../_shared/models/csTypes/time_item';
import { Penalty } from '../../_shared/models/csTypes/penalty';
import { Subject } from 'rxjs';
import { InfringementDetailsComponent } from './infringement-details/infringement-details.component';
import { InfringementService } from './infringement.service';
import { ToastService } from '../../_core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { PenaltyTypeEnum } from '../../_shared/models/csTypes/enums/penalty_type';
import { MatchUpStatusEnum } from '../../_shared/models/csTypes/enums/matchup_status';
import { CodeViolationTypeEnum } from '../../_shared/models/csTypes/enums/codeViolationTypeEnum';
import { WinReasonEnum } from '../../_shared/models/csTypes/enums/win_reason';
import { IocSelectorOption, IocSelectorService } from '../../_shared/components/ioc-selector/ioc-selector.service';

@Component({
  standalone: false,
    selector: 'app-infringement-list',
    templateUrl: './infringement-list.component.html',
    styleUrls: ['./infringement-list.component.scss'],
})

export class InfringementListComponent implements OnInit, OnDestroy {
    @Input() match: Match;
    @Input() availableHeight: number;
    @Input() side1Color: string;
    @Input() side2Color: string;
    @Output() addPoint: EventEmitter<{ sideNumber: number, winReason: WinReasonEnum }> = new EventEmitter();
    @Output() addGame: EventEmitter<{ sideNumber: number, winReason: WinReasonEnum }> = new EventEmitter();

    infringementTimeItems: TimeItem[] = [];
    participantSide1: string;
    participantSide2: string;

    private readonly destroy$: Subject<void> = new Subject();
    isTablet: boolean;
    iocSelectorService = inject(IocSelectorService);

    constructor(
        private actionSheetController: ActionSheetController,
        private bhs: BoardHelpService,
        private infringementService: InfringementService,
        private matchService: MatchService,
        private modalController: ModalController,
        private toast: ToastService,
        private routerOutlet: IonRouterOutlet,
        private translateService: TranslateService,
        private platform: Platform,
        private cd: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.isTablet = this.platform.is('tablet');
        this.match.timeItems.sort((a, b) => new Date(b.itemDate).getTime() - new Date(a.itemDate).getTime());
        this.infringementTimeItems = this.infringementService.getInfringementTimeItems(this.match);
        this.participantSide1 = this.bhs.getPlayersNames(this.match, 1, 'full');
        this.participantSide2 = this.bhs.getPlayersNames(this.match, 2, 'full');
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    filterEvents() {
        // To do
    }

    async addInfringementsOrInterruption() {
        const title = this.translateService.instant('Select Violation Type');
        const showCancelButton = true;
        const iocSelectorOptions: IocSelectorOption[] = [
            {
                label: this.translateService.instant('Overrule'),
                value: 'overrule'
            },
            {
                label: this.translateService.instant('Interruption / Toilet Break'),
                value: 'interruption'
            },
            {
                label: this.translateService.instant('Time violation'),
                value: 'timeViolation'
            },
            {
                label: this.translateService.instant('Code violation'),
                value: 'codeViolation'
            }
        ]

        const violationType =  await this.iocSelectorService.openPopover(title, iocSelectorOptions, showCancelButton);

        switch (violationType) {
            case 'overrule':
                this.addOverrule()
                break;
            case 'interruption':
                this.addInterruption()
                break;
            case 'timeViolation':
                this.addTimeViolation()
                break;
            case 'codeViolation':
                this.addCodeOfConduct()
                break;
        
            default:
                break;
        }
    }

    async addOverrule(sideNumber?) {
        const selectOverruleLocation: string = await this.selectOverruleLocation();

        if (!sideNumber) {
            sideNumber = await this.bhs.selectSide(this.match, true, 'Select participant');
            if (!sideNumber) return;
        }

        let overrule: Infringement = {
            infringementType: InfringementType.OVERRULE,
            sideNumber: sideNumber,
        };

        const note = `Overruled line: ${selectOverruleLocation}`;

        overrule = { ...overrule, ...this.infringementService.addContextToInfringement(this.match, note) };

        const timeItem: TimeItem = this.infringementService.generateTimeItem(overrule);
        await this.openInfringementDetails(timeItem, true);
    }

    async selectOverruleLocation(): Promise<string> {
        const overruleLocations = ['Near Side', 'Far Side', 'Baseline', 'Center Service Line', 'Service Line', 'Service Let'];
        const iocSelectorOptions: IocSelectorOption[] = [];

        this.translateService.get(overruleLocations)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                translate => {
                    overruleLocations.forEach((overruleLocation) => {
                        iocSelectorOptions.push({
                            label: translate[overruleLocation],
                            value: overruleLocation
                        });
                    });
                }
            );
        
        const title = this.translateService.instant('Overruled Line');
        const showCancelButton = true;

        const selectedOverruleLocation =  await this.iocSelectorService.openPopover(title, iocSelectorOptions, showCancelButton);

        if (selectedOverruleLocation) {
            return selectedOverruleLocation;
        }

    }

    async addInterruption(sideNumber?: number) {

        const interruptionReason: InterruptionReason = await this.getInterruptionReason();
        if (!interruptionReason) return;

        if (interruptionReason !== InterruptionReason.MATCH_SUSPENSION) {
            if (!sideNumber) {
                sideNumber = await this.bhs.selectSide(this.match, true, 'Select participant');
                if (!sideNumber) return;
            }
        }

        let interruption: Infringement = {
            infringementType: InfringementType.INTERRUPTION,
            interruptionReason,
            sideNumber
        };

        interruption = { ...interruption, ...this.infringementService.addContextToInfringement(this.match) };

        const timeItem: TimeItem = this.infringementService.generateTimeItem(interruption);
        await this.openInfringementDetails(timeItem, true);

    }

    async addTimeViolation(sideNumber?: number) {
        if (!sideNumber) {
            sideNumber = await this.bhs.selectSide(this.match, true, 'Select participant');
            if (!sideNumber) return;
        }

        let timeViolation: Infringement = {
            infringementType: InfringementType.TIME_VIOLATION,
            sideNumber
        };

        timeViolation = { ...timeViolation, ...this.infringementService.addContextToInfringement(this.match) };

        const timeItem: TimeItem = this.infringementService.generateTimeItem(timeViolation);
        this.openInfringementDetails(timeItem, true);
    }


    async addCodeOfConduct(sideNumber?: number) {

        const penalty: Penalty = await this.getPenalty();
        if (!penalty) return;

        if (!sideNumber) {
            sideNumber = await this.bhs.selectSide(this.match, true, 'Select participant');
            if (!sideNumber) return;
        }

        let timeViolation: Infringement = {
            infringementType: InfringementType.CODE_VIOLATION,
            sideNumber: sideNumber,
            penalty: penalty
        };

        timeViolation = { ...timeViolation, ...this.infringementService.addContextToInfringement(this.match) };

        const timeItem: TimeItem = this.infringementService.generateTimeItem(timeViolation);
        this.openInfringementDetails(timeItem, true);

    }

    async openInfringementDetails(timeItem: TimeItem, isCreate = false) {
        const modal = await this.modalController.create({
            component: InfringementDetailsComponent,
            presentingElement: this.routerOutlet.nativeEl,
            componentProps: {
                infringementTimeItem: timeItem,
                isCreate: isCreate,
                match: this.match
            }
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data?.action === 'DELETE') {

            this.deleteItem(timeItem);

        } else if (data?.action === 'EDIT') {

            timeItem = data.value;
            this.editItem(timeItem);

        } else if (data?.action === 'CREATE') {

            timeItem = data.value;
            this.createTimeItem(timeItem);

        }
    }

    private async getPenalty(): Promise<Penalty> {
        const codeViolationType = await this.getCodeViolationType();
        if (!codeViolationType) return;

        const penaltyType: PenaltyTypeEnum[] = [PenaltyTypeEnum.UNREASONABLE_DELAYS, PenaltyTypeEnum.AUDIBLE_OBSCENITY, PenaltyTypeEnum.VISIBLE_OBSCENITY, PenaltyTypeEnum.BALL_ABUSE, PenaltyTypeEnum.RACQUET_ABUSE, PenaltyTypeEnum.VERBAL_ABUSE, PenaltyTypeEnum.PHYSICAL_ABUSE, PenaltyTypeEnum.COACHING, PenaltyTypeEnum.UNSPORTSMANLIKE_CONDUCT];
        const iocSelectorOptions: IocSelectorOption[] = [];

        this.translateService.get(penaltyType)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                translate => {
                    penaltyType.forEach((ir) => {
                        iocSelectorOptions.push({
                            label: translate[ir],
                            value: ir
                        });
                    })
                }
            );

        const title = 'Code Violation';
        const showCancelButton = true;
        const selectedPenalty =  await this.iocSelectorService.openPopover(title, iocSelectorOptions, showCancelButton);

        if (selectedPenalty) {
            const penalty: Penalty = {
                penaltyType: selectedPenalty,
                matchUpId: this.match.matchId,
                codeViolationType: codeViolationType
            };

            return penalty;
        }
    }

    async getCodeViolationType() {        
        const codeViolationType: CodeViolationTypeEnum[] = [CodeViolationTypeEnum.WARNING, CodeViolationTypeEnum.POINT, CodeViolationTypeEnum.GAME, CodeViolationTypeEnum.DEFAULT];
        const iocSelectorOptions: IocSelectorOption[] = [];

        this.translateService.get(codeViolationType)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                translate => {
                    codeViolationType.forEach((ir) => {
                        iocSelectorOptions.push({
                            label: translate[ir],
                            value: ir
                        });
                    });
                }
            );

        const title = 'Penalty';
        const showCancelButton = true;
        const selectedCodeViolation =  await this.iocSelectorService.openPopover(title, iocSelectorOptions, showCancelButton);

        if (selectedCodeViolation) {
            return selectedCodeViolation;
        }
    }

    private async getInterruptionReason(): Promise<InterruptionReason> {
        const interruptionReasons: InterruptionReason[] = [InterruptionReason.MEDICAL_TIMEOUT, InterruptionReason.TOILET_BREAK, InterruptionReason.PLAYER_RETIREMENT, InterruptionReason.MATCH_SUSPENSION];
        const iocSelectorOptions: IocSelectorOption[] = [];

        this.translateService.get(interruptionReasons)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                translate => {
                    interruptionReasons.forEach((ir) => {
                        iocSelectorOptions.push({
                            label: translate[ir],
                            value: ir
                        });
                    });
                }
            );

        const title = 'Interruption reason';
        const showCancelButton = true;
        const selectedInterruptionReason =  await this.iocSelectorService.openPopover(title, iocSelectorOptions, showCancelButton);

        if (selectedInterruptionReason) {
            return selectedInterruptionReason;
        }
    }

    private deleteItem(timeItem: TimeItem) {
        const timeItemIndex = this.match.timeItems.findIndex(ti => ti.itemId === timeItem.itemId);
        this.match.timeItems.splice(timeItemIndex, 1);
        this.infringementTimeItems = this.infringementService.getInfringementTimeItems(this.match);

        this.matchService
            .deleteTimeItem(this.match._id, timeItem.itemId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                res => {
                    this.toast.presentToast(res.message);
                    this.infringementService.publishUpdateViolations(this.match);
                }
            );
    }

    private editItem(timeItem: TimeItem) {
        const timeItemIndex = this.match.timeItems.findIndex(ti => ti.itemId === timeItem.itemId);

        this.match.timeItems[timeItemIndex] = timeItem;
        this.infringementTimeItems = this.infringementService.getInfringementTimeItems(this.match);

        this.matchService
            .updateTimeItem(this.match._id, timeItem.itemId, timeItem.itemValue)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                res => {
                    this.toast.presentToast(res.message);
                    this.infringementService.publishUpdateViolations(this.match);
                }
            );
    }

    private createTimeItem(timeItem: TimeItem) {        
        this.infringementTimeItems.unshift(timeItem);

        if (timeItem.itemValue.interruptionReason === InterruptionReason.PLAYER_RETIREMENT) {
            this.updateMatchStatus(timeItem.itemValue.interruptionReason, null, timeItem.itemValue.sideNumber);
        } else if (timeItem.itemValue.interruptionReason === InterruptionReason.MATCH_SUSPENSION) {
            this.updateMatchStatus(timeItem.itemValue.interruptionReason, null);
        } else if (timeItem.itemValue.penalty?.codeViolationType === CodeViolationTypeEnum.POINT) {
            this.addPoint.emit({ sideNumber: timeItem.itemValue.sideNumber === 1 ? 2 : 1, winReason: WinReasonEnum.PENALTY });
        } else if (timeItem.itemValue.penalty?.codeViolationType === CodeViolationTypeEnum.GAME) {
            this.addGame.emit({ sideNumber: timeItem.itemValue.sideNumber === 1 ? 2 : 1, winReason: WinReasonEnum.PENALTY });
        } else if (timeItem.itemValue.penalty?.codeViolationType === CodeViolationTypeEnum.DEFAULT) {
            this.updateMatchStatus(null, timeItem.itemValue.penalty.codeViolationType, timeItem.itemValue.sideNumber);
        }

        this.matchService
            .addTimeItem(this.match._id, timeItem)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                res => {
                    this.match.timeItems.unshift(timeItem);
                    this.infringementService.publishUpdateViolations(this.match);
                }
            );
    }

    private updateMatchStatus(interruptionReason: InterruptionReason, codeViolationType: CodeViolationTypeEnum, sideNumber?: number) {
        let matchStatus: string;
        switch (interruptionReason || codeViolationType) {
            case InterruptionReason.MATCH_SUSPENSION:
                matchStatus = MatchUpStatusEnum.SUSPENDED;
                break;
            case InterruptionReason.PLAYER_RETIREMENT:
                matchStatus = MatchUpStatusEnum.RETIRED;
                this.match.endDate = new Date();
                break;
            case CodeViolationTypeEnum.DEFAULT:
                matchStatus = MatchUpStatusEnum.DEFAULTED;
                this.match.endDate = new Date();
                break;

            default:
                break;
        }

        if (!matchStatus) {
            return;
        }

        this.match.matchStatus = matchStatus;

        if (sideNumber) {
            this.match.winningSide = sideNumber === 1 ? 2 : 1;
        }


        this.matchService
            .update(this.match, false)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                res => {
                    this.matchService.publishEdit(this.match);
                }
            );
    }

    isSuspendedItem(timeItem: TimeItem): boolean {
        if (!timeItem.itemValue.duration && timeItem.itemValue.interruptionReason === InterruptionReason.MATCH_SUSPENSION) {
            return true;
        }
    }

    getTimeItemColor(timeItem: TimeItem) {
        return timeItem.itemValue.sideNumber === 1 ? this.side1Color : this.side2Color;
    }

}
